<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import { cn } from '@/lib/utils'
import { childColorClass, getAvailableChildColors, type ChildColor } from '@/lib/colors'
import { buttonVariants, type ButtonColor, type ButtonColorVariant, type ButtonSize } from '.'
import HarmoniaPlusBadge from '@/components/premium/HarmoniaPlusBadge.vue'

// Lue à la SOURCE. Cette liste était recopiée à la main : le jour où la
// framboise est arrivée en sixième, un bouton `color="pink"` serait tombé dans
// la branche des couleurs de thème et aurait rendu une classe inexistante, en
// silence.
const CHILD_COLORS: readonly string[] = getAvailableChildColors()
type ChildColorLiteral = ChildColor

const props = withDefaults(defineProps<{
  title?: string
  size?: ButtonSize
  color?: ButtonColor
  colorVariant?: ButtonColorVariant
  expand?: 'block' | null
  textAlign?: 'center' | 'start' | 'end'
  isPremium?: boolean
  /**
   * Le bouton 3D (« slab ») : une tranche dure sous le bouton, qui s'écrase
   * quand on appuie. Réservé à l'action principale d'un écran. Il remplace les
   * ombres `0 6px 0 0 #C7912F` recopiées à la main de bouton en bouton.
   * Seulement avec `colorVariant="solid"` : un contour n'a pas d'épaisseur.
   */
  tactile?: boolean
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  class?: HTMLAttributes['class']
}>(), {
  size: 'md',
  color: 'primary',
  colorVariant: 'solid',
  expand: null,
  textAlign: 'center',
  isPremium: false,
  tactile: false,
  loading: false,
  disabled: false,
  type: 'button',
})

const emit = defineEmits<{ click: [event: MouseEvent] }>()

// Couleurs via tokens du thème. Les couleurs enfant utilisent les variables
// --child-* exposées par childColorClass() posée sur la racine.
function getColorClasses(): string {
  const color = props.color
  const variant = props.colorVariant

  if ((CHILD_COLORS as readonly string[]).includes(color)) {
    const map: Record<ButtonColorVariant, string> = {
      // Le blanc va sur le 900, pas sur le 700. L'intention d'origine était la
      // bonne (« que le texte blanc reste net sur les palettes enfant ») mais
      // le 700 était un cran trop clair : il donnait 3,93:1 sur l'ancien pêche
      // et 3,63:1 sur le miel, sous le seuil de 4,5. Le 900 les passe toutes
      // les six, du miel (5,24) au violet (7,84), et c'est d'ailleurs la règle
      // qu'index.css énonce déjà : « On the dark 900 shades, use white ».
      solid: 'bg-[var(--child-900)] text-white',
      outline: 'bg-transparent border border-[var(--child-500)] text-[var(--child-900)]',
      soft: 'bg-[var(--child-100)] text-[var(--child-900)]',
      dashed: 'bg-transparent border border-dashed border-[var(--child-500)] text-[var(--child-900)]',
      clear: 'bg-transparent text-[var(--child-900)]',
    }
    return map[variant]
  }

  const themed: Record<Exclude<ButtonColor, ChildColorLiteral>, Record<ButtonColorVariant, string>> = {
    primary: {
      solid: 'bg-brand text-brand-ink',
      outline: 'bg-transparent border border-brand text-brand-deep',
      soft: 'bg-brand-soft text-brand-deep',
      dashed: 'bg-transparent border border-dashed border-brand text-brand-deep',
      clear: 'bg-transparent text-brand-deep',
    },
    light: {
      solid: 'bg-white text-ink border border-line',
      outline: 'bg-transparent text-ink border border-line',
      soft: 'bg-warm-2 text-ink',
      dashed: 'bg-transparent text-ink border border-dashed border-line',
      clear: 'bg-transparent text-ink',
    },
    night: {
      solid: 'bg-night text-white',
      outline: 'bg-transparent border border-night text-night',
      soft: 'bg-night/10 text-night',
      dashed: 'bg-transparent border border-dashed border-night text-night',
      clear: 'bg-transparent text-night',
    },
    premium: {
      solid: 'bg-premium text-white',
      outline: 'bg-transparent border border-premium text-premium',
      soft: 'bg-premium-soft text-premium',
      dashed: 'bg-transparent border border-dashed border-premium text-premium',
      clear: 'bg-transparent text-premium',
    },
    // Rouge chaud du thème. Sans cette entrée, chaque action destructive
    // rehackait `!bg-red-600 !text-white` par-dessus le bouton primaire, ce qui
    // ramenait l'échelle Tailwind brute que la doctrine bannit.
    danger: {
      solid: 'bg-danger text-white',
      outline: 'bg-transparent border border-danger text-danger',
      soft: 'bg-danger-soft text-danger',
      dashed: 'bg-transparent border border-dashed border-danger text-danger',
      clear: 'bg-transparent text-danger',
    },
  }
  return themed[color as Exclude<ButtonColor, ChildColorLiteral>][variant]
}

const alignClass = computed(() => ({
  center: 'justify-center',
  start: 'justify-start',
  end: 'justify-end',
}[props.textAlign]))

const isChildColor = computed(() => CHILD_COLORS.includes(props.color))

// La tranche du bouton 3D, dans la teinte de son bouton : le jaune garde son
// bord doré, une couleur pleine s'assombrit d'un tiers.
const SLAB_EDGE: Record<Exclude<ButtonColor, ChildColorLiteral>, string> = {
  primary: 'var(--color-brand-edge)',
  light: 'var(--color-line-strong)',
  night: '#000',
  premium: 'color-mix(in srgb, var(--color-premium) 68%, #000)',
  danger: 'color-mix(in srgb, var(--color-danger) 68%, #000)',
}

const isTactile = computed(() => props.tactile && props.colorVariant === 'solid')

const slabStyle = computed(() => {
  if (!isTactile.value) return undefined
  const edge = isChildColor.value
    ? 'color-mix(in srgb, var(--child-900) 68%, #000)'
    : SLAB_EDGE[props.color as Exclude<ButtonColor, ChildColorLiteral>]
  return { '--slab-edge': edge }
})

// Les petites tailles ont une tranche plus fine : 6px sous un bouton de 32px,
// c'est un socle, plus un bouton.
const SLAB_THIN: ButtonSize[] = ['xs', 'sm', 'md']

const rootClasses = computed(() => cn(
  buttonVariants({ size: props.size }),
  isChildColor.value ? childColorClass(props.color) : '',
  getColorClasses(),
  alignClass.value,
  props.expand === 'block' ? 'w-full' : '',
  isTactile.value
    ? cn(
        'rounded-2xl active:scale-100 active:translate-y-0.5',
        SLAB_THIN.includes(props.size)
          ? 'shadow-[0_4px_0_0_var(--slab-edge)] active:shadow-[0_2px_0_0_var(--slab-edge)]'
          : 'shadow-[0_6px_0_0_var(--slab-edge)] active:shadow-[0_3px_0_0_var(--slab-edge)]',
      )
    : '',
  props.class,
))

const spinnerSize = computed(() => ({
  xs: 'size-3', sm: 'size-3.5', md: 'size-4', lg: 'size-5', xl: 'size-5', '2xl': 'size-6', '3xl': 'size-7',
}[props.size]))

function onClick(e: MouseEvent) {
  if (props.disabled || props.loading) return
  emit('click', e)
}
</script>

<template>
  <button :type="type" :disabled="disabled || loading" :class="rootClasses" :style="slabStyle" @click="onClick">
    <!-- Spinner prend la place du #start pendant le loading -->
    <span v-if="loading"
      :class="[spinnerSize, 'inline-block rounded-full border-2 border-current border-t-transparent animate-spin']"
      aria-hidden="true" />
    <slot v-else name="start" />

    <span v-if="title" class="truncate">{{ title }}</span>
    <slot v-else />

    <slot name="end" />
    <HarmoniaPlusBadge v-if="isPremium" />
  </button>
</template>
